import React, { useEffect, useState } from "react";
import { Card, Badge } from "react-bootstrap";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell,
  LabelList,
} from "recharts";
import axios from "axios";

const SalesChart = () => {
  const [weekData, setWeekData] = useState([]);
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth <= 767 : false
  );

  useEffect(() => {
    const fetchData = () => {
      axios
        .get("./dati_dashboard.json")
        .then((res) => {
          const weekOrdersData = res.data.AR.settimana || [];
          const sortedWeekData = [...weekOrdersData].sort(
            (a, b) => Number(a.ordine_giorno) - Number(b.ordine_giorno)
          );
          setWeekData(sortedWeekData);
        })
        .catch((err) => console.error(err));
    };

    fetchData();
  }, []);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 767);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const todayJs = new Date().getDay();
  const currentDayIndex = todayJs === 0 ? 7 : todayJs;
  const yesterdayIndex = currentDayIndex === 1 ? 7 : currentDayIndex - 1;
  const tomorrowIndex = currentDayIndex === 7 ? 1 : currentDayIndex + 1;

  const formatToEuro = (amount) =>
    new Intl.NumberFormat("it-IT", {
      style: "currency",
      currency: "EUR",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount || 0);

  const mobileData = [
    { order: yesterdayIndex, mobileLabel: "IERI" },
    { order: currentDayIndex, mobileLabel: "OGGI" },
    { order: tomorrowIndex, mobileLabel: "DOMANI" },
  ]
    .map(({ order, mobileLabel }) => {
      const found = weekData.find(
        (item) => Number(item.ordine_giorno) === order
      );
      return found ? { ...found, mobileLabel } : null;
    })
    .filter(Boolean);

  const chartData = isMobile ? mobileData : weekData;

  const renderTrendLabel = (props) => {
    const { x, y, width, index } = props;
    const dataItem = chartData[index];

    if (!dataItem) return null;

    const day = Number(dataItem.ordine_giorno);
    if (isMobile && day === tomorrowIndex) return null;

    const valore = parseFloat(dataItem.valore_ordinato);
    const media = parseFloat(dataItem.media_valore);

    if (!Number.isFinite(valore) || !Number.isFinite(media) || media === 0) {
      return null;
    }

    const trend = ((valore - media) / media) * 100;
    const color = trend >= 0 ? "#28a745" : "#dc3545";

    return (
      <text
        x={x + width / 2}
        y={y - 12}
        fill={color}
        textAnchor="middle"
        style={{
          fontSize: isMobile ? "9px" : "10px",
          fontWeight: "800",
        }}
      >
        {trend >= 0 ? "▲" : "▼"} {Math.abs(trend).toFixed(1)}%
      </text>
    );
  };

  const maxYValue =
    Math.max(
      0,
      ...chartData.map((d) =>
        Math.max(
          Number(d.valore_ordinato) || 0,
          Number(d.media_valore) || 0
        )
      )
    ) * 1.15;

  const renderCustomLegend = () => (
    <div className="am-week-legend">
      <div>
        <span className="am-week-dot am-week-dot-orange" />
        <span>Ordinato</span>
      </div>

      <div>
        <span className="am-week-dot am-week-dot-gray" />
        <span>Media (6m)</span>
      </div>
    </div>
  );

  return (
    <Card
      className="card ombra card-week bg-dark card-home hover-effect border-0 am-chart-card"
      style={{ borderRadius: "15px" }}
    >
      <div className="d-flex justify-content-between align-items-center px-3 am-chart-header">
        <div>
          <h6
            className="text-title2 fw-bold mb-0"
            style={{ letterSpacing: "1px" }}
          >
            PERFORMANCE SETTIMANALE
          </h6>

          <small
            className="text-light"
            style={{ fontSize: "0.65rem" }}
          >
            Confronto valore ordinato vs media 6 mesi
          </small>
        </div>

        <Badge className="am-badge-orange">
          WEEKLY
        </Badge>
      </div>

      <div className="am-chart-body">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={chartData}
            margin={{ top: 30, right: 10, left: 0, bottom: 0 }}
            barGap={isMobile ? 10 : 8}
          >
            <XAxis
              dataKey={isMobile ? "mobileLabel" : "it_nome_giorno"}
              axisLine={false}
              tickLine={false}
              tick={{
                fill: "#9ca0a8",
                fontSize: isMobile ? 10 : 11,
                fontWeight: "700",
              }}
              tickFormatter={(value) =>
                isMobile
                  ? value
                  : String(value).substring(0, 3).toUpperCase()
              }
            />

            <YAxis
              hide
              domain={[0, maxYValue || "auto"]}
            />

            <Tooltip
              cursor={{ fill: "rgba(255,255,255,0.025)" }}
              contentStyle={{
                borderRadius: "12px",
                border: "1px solid rgba(255,255,255,0.1)",
                background: "rgba(20,22,27,0.96)",
                color: "#fff",
                boxShadow: "0 10px 30px rgba(0,0,0,0.28)",
                fontSize: "12px",
              }}
              labelStyle={{
                color: "#fff",
                fontWeight: "700",
              }}
              formatter={(value) => [formatToEuro(value), ""]}
            />

            <Legend content={renderCustomLegend} />

            <Bar
              dataKey="valore_ordinato"
              name="Ordinato"
              fill="#f16f24"
              radius={[6, 6, 0, 0]}
              barSize={isMobile ? 36 : 40}
            >
              <LabelList content={renderTrendLabel} />

              {chartData.map((entry, index) => (
                <Cell
                  key={`cell-v-${index}`}
                  fill="#f16f24"
                  fillOpacity={
                    Number(entry.ordine_giorno) === tomorrowIndex
                      ? 0.22
                      : 1
                  }
                />
              ))}
            </Bar>

            <Bar
              dataKey="media_valore"
              name="Media (6m)"
              fill="#5a5a5a"
              radius={[6, 6, 0, 0]}
              barSize={isMobile ? 36 : 40}
            >
              {chartData.map((entry, index) => (
                <Cell
                  key={`cell-m-${index}`}
                  fill="#5a5a5a"
                  fillOpacity={
                    Number(entry.ordine_giorno) === tomorrowIndex
                      ? 0.22
                      : 0.8
                  }
                />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </Card>
  );
};

export default SalesChart;
